import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	EARLY_FREEDOM_ANCHOR_COMFORT,
	EARLY_FREEDOM_ANCHOR_LUXURY,
	EARLY_FREEDOM_ANCHOR_MODEST,
	EARLY_FREEDOM_SAVING_FALLBACK,
	EARLY_FREEDOM_TARGET_FALLBACK,
} from "web_myplann/Inspiration/constants";
import { MONTHS_IN_YEAR } from "web_myplann/Shared/constants";

const SAFE_WITHDRAWAL_YEARS = 25;
const EARLY_FREEDOM_READY_COVERAGE = 90;
const EARLY_FREEDOM_MAX_YEARS = 60;
const EXAMPLE_TARGET_RENT = 15_000;

const RATE_CONSERVATIVE = 0.04;
const RATE_BALANCED = 0.06;
const RATE_DYNAMIC = 0.08;

export type EarlyFreedomLevel = "cold" | "lite" | "full";
export type EarlyFreedomScenarioKey = "conservative" | "balanced" | "dynamic";
export type EarlyFreedomAnchorKey = "modest" | "comfort" | "luxury";

export type EarlyFreedomInputs = {
	targetExpenses?: number;
	monthlySaving?: number;
	rentNet?: number;
};

export type EarlyFreedomScenario = {
	key: EarlyFreedomScenarioKey;
	rate: number;
	years: number | null;
	targetAge: number | null;
};

export type EarlyFreedomContext = {
	level: EarlyFreedomLevel;
	age?: number;
	targetExpenses: number;
	monthlySaving: number;
	rentNet: number;
	passiveToday: number;
	gap: number;
	coverageRatio: number;
	isEarlyFreedomReady: boolean;
	targetCapital: number;
	scenarios: EarlyFreedomScenario[];
	activeAnchorKey: EarlyFreedomAnchorKey;
};

const ANCHOR_KEY_BY_AMOUNT = new Map<number, EarlyFreedomAnchorKey>([
	[EARLY_FREEDOM_ANCHOR_MODEST, "modest"],
	[EARLY_FREEDOM_ANCHOR_COMFORT, "comfort"],
	[EARLY_FREEDOM_ANCHOR_LUXURY, "luxury"],
]);

const futureValueOfMonthlySavings = (
	monthlyContribution: number,
	monthlyRate: number,
	months: number,
): number =>
	monthlyRate > 0
		? monthlyContribution * (((1 + monthlyRate) ** months - 1) / monthlyRate)
		: monthlyContribution * months;

const yearsToEarlyFreedom = (
	monthlyContribution: number,
	yearlyRate: number,
	targetCapital: number,
): number | null => {
	if (targetCapital <= 0) return 0;
	if (monthlyContribution <= 0) return null;

	const monthlyRate = yearlyRate / MONTHS_IN_YEAR;

	for (let years = 1; years <= EARLY_FREEDOM_MAX_YEARS; years++) {
		const savedCapital = futureValueOfMonthlySavings(
			monthlyContribution,
			monthlyRate,
			years * MONTHS_IN_YEAR,
		);
		if (savedCapital >= targetCapital) return years;
	}

	return null;
};

const deriveLevel = (
	targetExpenses: number | undefined,
	passiveToday: number,
): EarlyFreedomLevel => {
	if (targetExpenses === undefined) return "cold";
	if (passiveToday > 0) return "full";
	return "lite";
};

type Baseline = {
	age?: number;
	monthlySaving?: number;
};

export const computeEarlyFreedomContext = (
	inputs: EarlyFreedomInputs,
	baseline: Baseline = {},
): EarlyFreedomContext => {
	const targetExpenses = inputs.targetExpenses ?? EARLY_FREEDOM_TARGET_FALLBACK;
	const monthlySaving =
		inputs.monthlySaving ??
		baseline.monthlySaving ??
		EARLY_FREEDOM_SAVING_FALLBACK;
	const rentNet = inputs.rentNet ?? 0;
	const passiveToday = rentNet;

	const gap = Math.max(0, targetExpenses - passiveToday);
	const coverageRatio =
		targetExpenses > 0
			? Math.min(100, Math.round((passiveToday / targetExpenses) * 100))
			: 0;
	const targetCapital = gap * MONTHS_IN_YEAR * SAFE_WITHDRAWAL_YEARS;
	const level = deriveLevel(inputs.targetExpenses, passiveToday);

	const scenarioFor = (
		key: EarlyFreedomScenarioKey,
		rate: number,
	): EarlyFreedomScenario => {
		const years = yearsToEarlyFreedom(monthlySaving, rate, targetCapital);
		return {
			key,
			rate,
			years,
			targetAge:
				baseline.age !== undefined && years !== null
					? baseline.age + years
					: null,
		};
	};

	const activeAnchorKey = ANCHOR_KEY_BY_AMOUNT.get(targetExpenses) ?? "luxury";

	return {
		level,
		age: baseline.age,
		targetExpenses,
		monthlySaving,
		rentNet,
		passiveToday,
		gap,
		coverageRatio,
		isEarlyFreedomReady:
			level !== "cold" && coverageRatio >= EARLY_FREEDOM_READY_COVERAGE,
		targetCapital,
		scenarios: [
			scenarioFor("conservative", RATE_CONSERVATIVE),
			scenarioFor("balanced", RATE_BALANCED),
			scenarioFor("dynamic", RATE_DYNAMIC),
		],
		activeAnchorKey,
	};
};

export type EarlyFreedomGoalPreset = {
	monthlyRent: number;
	dueDate: Date;
};

export const buildEarlyFreedomGoalPreset = (
	context: EarlyFreedomContext,
): EarlyFreedomGoalPreset => {
	const balanced = context.scenarios.find((s) => s.key === "balanced");
	const years = balanced?.years ?? SAFE_WITHDRAWAL_YEARS;
	const now = new Date();
	return {
		monthlyRent: context.gap,
		dueDate: new Date(now.getFullYear() + years, now.getMonth(), 1),
	};
};

export const EARLY_FREEDOM_GOAL_CATEGORY = GoalCategoryTypeEnum.Rent;

const earlyFreedomExample = computeEarlyFreedomContext({
	targetExpenses: EARLY_FREEDOM_TARGET_FALLBACK,
	rentNet: EXAMPLE_TARGET_RENT,
});

export const EARLY_FREEDOM_EXAMPLE = {
	spend: earlyFreedomExample.targetExpenses,
	rent: earlyFreedomExample.passiveToday,
	gap: earlyFreedomExample.gap,
	capital: earlyFreedomExample.targetCapital,
};
